import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
// mui
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { Button } from '@mui/material';
// component
import networkList, { resNetworkList } from '../../component/wallet/NetworkList';
// hook
import { useMultiBalance, TokenBalance } from '@/hooks/hookWagmi/useMultiBalance';
// lib & store
import { inter } from '@/theme';
import { truncateWeiTo6Decimals } from '@/lib/web3/utils';
import { useWeb3AuthConnect, useWeb3Auth } from '@web3auth/modal/react';
import { useChainId } from 'wagmi';

// 보안을 위한 타입 검증 함수
const validateNetworkData = (network: any): network is resNetworkList => {
  return (
    network &&
    typeof network.name === 'string' &&
    typeof network.symbol === 'string' &&
    (network.token === undefined || typeof network.token === 'string') &&
    network.icon !== undefined
  );
};

function Status({
  symbol,
  icon,
  balanceData,
}: {
  symbol: string;
  icon: React.ReactNode;
  balanceData: TokenBalance | undefined;
}) {
  const navigate = useNavigate();

  const handleClick = () => {
    if (!symbol || typeof symbol !== 'string') {
      console.error('유효하지 않은 심볼:', symbol);
      return;
    }

    navigate('/walletDetail', {
      state: {
        symbol: symbol,
      },
    });
  };

  const formattedBalance = balanceData
    ? truncateWeiTo6Decimals(balanceData.balance, balanceData.decimals) || '0'
    : '0';

  return (
    <Stack
      onClick={handleClick}
      borderRadius={2}
      bgcolor="#F5F5F5"
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      px={2.5}
      py={2}
    >
      <Stack direction="row" alignItems="center" gap={1.5}>
        <Stack width={48} height={48} alignItems="center" justifyContent="center" borderRadius="50%" bgcolor="white">
          {icon}
        </Stack>
      </Stack>
      <Box>
        <Typography variant="h4" fontWeight={700} fontFamily={inter.style.fontFamily} textAlign="right">
          {balanceData?.symbol || symbol} {formattedBalance}
        </Typography>
      </Box>
    </Stack>
  );
}

export default function CoinStatus() {
  const [networks, setNetworks] = useState<resNetworkList[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { web3Auth } = useWeb3Auth();
  const [chainId, setChainId] = useState('');
  const { isConnected } = useWeb3AuthConnect();
  const currentChainId = useChainId();

  // Multicall로 모든 토큰 잔액 한 번에 조회 (모든 조회 완료 시 동시 표시)
  const { balances, isLoading: balancesLoading, isAllLoaded } = useMultiBalance(networks);

  // 네트워크 리스트를 비동기로 로드
  useEffect(() => {
    if (!isConnected) {
      return;
    }

    const loadNetworks = async () => {
      try {
        setIsLoading(true);
        setError(null);

        if (!currentChainId || typeof currentChainId !== 'number') {
          throw new Error('유효하지 않은 체인 ID');
        }

        const res: resNetworkList[] = await networkList(currentChainId);
        const validatedNetworks = res.filter(validateNetworkData);

        if (validatedNetworks.length === 0) {
          throw new Error('유효한 네트워크 데이터를 찾을 수 없습니다');
        }

        setChainId(web3Auth?.currentChain?.displayName || '');
        setNetworks(validatedNetworks);
      } catch (err) {
        console.error('네트워크 로딩 오류:', err);
        setError(err instanceof Error ? err.message : '네트워크 로딩 중 오류가 발생했습니다');
        setNetworks(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadNetworks();
  }, [isConnected, currentChainId]);

  // 로딩 상태 표시
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
        <Typography variant="body1" ml={2}>
          네트워크 정보를 불러오는 중...
        </Typography>
      </Box>
    );
  }

  // 에러 상태 표시
  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
        <Button onClick={() => window.location.reload()} sx={{ ml: 2 }} variant="outlined" size="small">
          다시 시도
        </Button>
      </Alert>
    );
  }

  return (
    <>
      MainNet: {chainId}
      {networks ? (
        <Stack gap={1.5}>
          {/* 모든 잔액 조회가 완료될 때까지 로딩 표시 */}
          {balancesLoading || !isAllLoaded ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
              <CircularProgress size={24} />
              <Typography variant="body2" ml={2}>
                잔액 조회 중...
              </Typography>
            </Box>
          ) : (
            networks.map((coin) => (
              <Status
                key={`coin-status-${coin.name}`}
                symbol={coin.symbol}
                icon={coin.icon}
                balanceData={balances.get(coin.symbol)}
              />
            ))
          )}
        </Stack>
      ) : (
        <Typography variant="body1" textAlign="center" color="text.secondary">
          네트워크 정보를 불러올 수 없습니다
        </Typography>
      )}
    </>
  );
}
